import i18nFactory from '@yandex-int/i18n';
import { includes } from 'lodash';
import moment from 'moment';

import {
    HeaderTabsData,
    Lang,
    LinkTarget,
    NavLinkData,
    Tld
} from '../../typings/common';
import { GdMenuItem } from '../../typings/graphql-grid';
import { LocalStorage, StorageKeyType } from '../Storage/Storage';
import { UrlHelpers } from '../UrlHelpers';

import * as keyset from './HeaderHelpers.i18n';

const i18n = i18nFactory(keyset);

export class HeaderHelpers {
    public static getTableauServices(lang: Lang): Array<string> {
        switch (lang) {
            case Lang.ru:
                return [
                    'metrika',
                    'partners',
                    'direct-api',
                    'commander',
                    'market',
                    'kassa',
                    'money',
                    'sprav',
                    'telephony',
                    'mail',
                    'audience',
                    'surveys'
                ];
            default:
                return [
                    'metrika',
                    'partners-en',
                    'direct-api',
                    'market',
                    'kassa',
                    'money',
                    'sprav',
                    'disk',
                    'mail',
                    'audience'
                ];
        }
    }

    public static getInfoMenuLinks(tld: Tld, lang: Lang): Array<NavLinkData> {
        return [
            {
                url: UrlHelpers.getCommonHelpUrl('direct', tld),
                text: i18n('help')
            },
            {
                lang: [Lang.ru, Lang.uk, Lang.en],
                url: 'https://yandex.ru/blog/adv',
                text: i18n('site')
            },
            {
                lang: [Lang.ru],
                url:
                    'https://yandex.ru/adv/edu?utm_source=direct&utm_medium=main_popup_question&utm_campaign=static',
                text: i18n('study')
            },
            {
                lang: [Lang.ru],
                url:
                    'https://yandex.ru/adv/story/cases/?utm_source=direct&utm_medium=main_popup_question&utm_campaign=static',
                text: i18n('cases')
            },
            {
                lang: [Lang.en],
                url:
                    'https://yandex.com/adv/edu/direct-start?utm_source=direct&utm_medium=product-header&utm_campaign=static_en',
                text: i18n('online-courses')
            },
            {
                lang: [Lang.en],
                url:
                    'https://yandex.com/adv/story/cases?utm_source=direct&utm_medium=product-header&utm_campaign=static_en',
                text: i18n('cases')
            },
            {
                lang: [Lang.uk],
                url:
                    'https://yandex.ua/adv/edu?utm_source=direct&utm_medium=product-header&utm_campaign=static_ua',
                text: i18n('study')
            },
            {
                lang: [Lang.tr],
                tld: [Tld.comTr],
                url:
                    'https://yandex.com/adv/edu/direct-start?utm_source=direct&utm_medium=product-header&utm_campaign=static_tr',
                text: i18n('study')
            }
        ]
            .filter(item => {
                return (
                    (!item.tld || item.tld.includes(tld)) &&
                    (!item.lang || item.lang.includes(lang))
                );
            })
            .map(({ url, text }) => {
                return { location: url, title: text, target: LinkTarget.blank };
            });
    }

    public static getAllowedHeaderTabs(
        headerTabs: Array<HeaderTabsData>,
        allowedHeaderTabs: GdMenuItem[]
    ): Array<NavLinkData> {
        return headerTabs.reduce(
            (res, item) => {
                const {
                    title,
                    getTitle,
                    location,
                    target,
                    isExternal,
                    isActive,
                    group,
                    description,
                    icon,
                    onClick
                } = item;

                const headerTab: NavLinkData = {
                    title,
                    getTitle,
                    location,
                    target,
                    isExternal,
                    isActive,
                    group,
                    description,
                    icon,
                    onClick
                };

                if (item.group && !item.key) {
                    headerTab.group = HeaderHelpers.getAllowedHeaderTabs(
                        item.group,
                        allowedHeaderTabs
                    );

                    if (headerTab.group.length > 0) {
                        res.push(headerTab);
                    }
                } else if (
                    includes(allowedHeaderTabs, item.key) &&
                    item.isAvailable
                ) {
                    res.push(headerTab);
                }

                return res;
            },
            [] as Array<NavLinkData>
        );
    }

    public static initFeedbackPromo(): void {
        const newInterfacePromoShowTime = HeaderHelpers.getNewInterfacePromoShowTime();

        if (!newInterfacePromoShowTime && newInterfacePromoShowTime !== 0) {
            HeaderHelpers.setNewInterfacePromoShowTime();
        }
    }

    public static isFeedbackPromoNeedToShow(): boolean {
        const newInterfacePromoShowTime = HeaderHelpers.getNewInterfacePromoShowTime();

        if (
            newInterfacePromoShowTime &&
            newInterfacePromoShowTime <= moment().valueOf()
        ) {
            return true;
        }

        return false;
    }

    public static resetNewInterfacePromoShowTime(): void {
        LocalStorage.setItem(StorageKeyType.newInterfacePromoShowTime, 0);
    }

    public static isRecommendationsTooltipHidden(): boolean {
        return Boolean(
            LocalStorage.getItem(StorageKeyType.recommendationTooltipHidden)
        );
    }

    public static hideRecommendationsTooltip(): void {
        LocalStorage.setItem(StorageKeyType.recommendationTooltipHidden, true);
    }

    private static setNewInterfacePromoShowTime(): void {
        const threeDaysInMilliSeconds = moment()
            .add(3, 'days')
            .valueOf();

        LocalStorage.setItem(
            StorageKeyType.newInterfacePromoShowTime,
            threeDaysInMilliSeconds
        );
    }

    private static getNewInterfacePromoShowTime = (): number | undefined =>
        LocalStorage.getItem(StorageKeyType.newInterfacePromoShowTime) as
            | number
            | undefined;
}
